import React, { useEffect, useState } from "react";
import { EntityLink } from "@/components/EntityLink";
import { api, euro } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HelpTip } from "@/components/HelpTip";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HandCoins, Save, BadgeCheck } from "lucide-react";

const eur = (n) => euro(n);

export default function AdminAgentCommissions() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/agent-commissions").then((r) => { setCfg(r.data.config); setAgents(r.data.agents); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setC = (k, v) => setCfg((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/agent-config", {
        recharge_percent: parseFloat(cfg.recharge_percent) || 0,
        gettone_dealer: parseFloat(cfg.gettone_dealer) || 0,
        gettone_master: parseFloat(cfg.gettone_master) || 0,
        commission_emails: cfg.commission_emails !== false,
      });
      toast.success("Configurazione provvigioni salvata");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // payout
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const openPay = (a) => { setPayTarget(a); setPayAmount(String(a.outstanding || 0)); setPayNote(""); };
  const doPayout = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) { toast.error("Importo non valido"); return; }
    setPayBusy(true);
    try {
      await api.post("/admin/agent-payouts", { agent_id: payTarget.id, amount: amt, note: payNote || null });
      toast.success(`Liquidati ${eur(amt)} a ${payTarget.name}`);
      setPayTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella liquidazione"); }
    finally { setPayBusy(false); }
  };

  // history
  const [histAgent, setHistAgent] = useState(null);
  const [hist, setHist] = useState([]);
  const openHist = async (a) => {
    setHistAgent(a); setHist([]);
    try { const { data } = await api.get("/admin/agent-payouts", { params: { agent_id: a.id } }); setHist(data); }
    catch { toast.error("Errore nel caricamento storico"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-agent-commissions-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><HandCoins className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Amministrazione</span></div>
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Provvigioni Agenti</h1>
          <p className="text-sm text-muted-foreground mt-1">Le agenzie guadagnano solo dalla ripartizione di rete e dalle quote sulle ricariche. Qui registri i pagamenti.</p>
        </div>
        <HelpTip cat="provvigioni" />
      </div>

      {cfg && (
        <Card className="p-4 glow-border flex flex-wrap items-center justify-between gap-3" data-testid="agent-config-card">
          <p className="text-sm text-muted-foreground" data-testid="agent-net-wallet-note">Percentuali in <a className="text-primary underline" href="/app/provvigioni-energia" data-testid="agent-split-link">Ripartizione provvigioni di rete</a> e quote ricariche nel <a className="text-primary underline" href="/app/wallet-admin#wallet-commission-policy" data-testid="agent-wallet-policy-link">Wallet Admin</a>.</p>
          <div className="flex items-center gap-2"><Label className="text-sm">Email riepilogo settimanale</Label><Switch checked={cfg.commission_emails !== false} onCheckedChange={(v) => setC("commission_emails", v)} data-testid="cfg-commission-emails" /><Button size="sm" onClick={save} disabled={busy} className="gap-2" data-testid="cfg-save"><Save className="w-4 h-4" />Salva</Button></div>
        </Card>
      )}

      <Card className="glow-border">
        <div className="px-5 py-4 border-b border-border"><h2 className="font-head font-semibold">Maturato e liquidazioni</h2></div>
        <Table>
          <TableHeader><TableRow><TableHead>Agente</TableHead><TableHead>Maturato</TableHead><TableHead>Liquidato</TableHead><TableHead>Da liquidare</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {agents.map((a) => (
              <TableRow key={a.id} data-testid={`agent-row-${a.id}`}>
                <TableCell><div className="font-medium"><EntityLink id={a.id} data-testid={`agent-commission-profile-link-${a.id}`}>{a.name}</EntityLink></div><div className="text-xs text-muted-foreground font-mono">{a.agent_code || a.email}</div></TableCell>
                <TableCell className="font-medium">{eur(a.total)}</TableCell>
                <TableCell className="text-teal-500">{eur(a.paid_total)}</TableCell>
                <TableCell className="text-amber-500 font-semibold">{eur(a.outstanding)}</TableCell>
                <TableCell className="flex gap-1 justify-end">
                  {a.payout_requested && <span className="text-xs font-semibold text-amber-600 self-center" data-testid={`payout-requested-${a.id}`}>Liquidazione richiesta</span>}
                  <Button size="sm" variant="ghost" onClick={async () => { try { await impersonate(a.id); toast.success(`Ora sei nell'area di ${a.name}`); navigate("/app/dashboard"); } catch (e) { toast.error(e.response?.data?.detail || "Impossibile accedere"); } }} data-testid={`agent-impersonate-${a.id}`}>Accedi come</Button>
                  <Button size="sm" variant="outline" onClick={() => openHist(a)} data-testid={`payout-history-${a.id}`}>Storico</Button>
                  <Button size="sm" className="gap-1 bg-teal-600 hover:bg-teal-700" disabled={a.outstanding <= 0} onClick={() => openPay(a)} data-testid={`payout-open-${a.id}`}><BadgeCheck className="w-4 h-4" /> Liquida</Button>
                </TableCell>
              </TableRow>
            ))}
            {agents.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nessun agente registrato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent data-testid="payout-dialog">
          <DialogHeader><DialogTitle>Liquida provvigioni · {payTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Da liquidare: <strong className="text-amber-500">{eur(payTarget?.outstanding)}</strong>. Registra il pagamento (puoi liquidare tutto o un importo parziale).</p>
            <div><Label>Importo (€)</Label><Input type="number" min="0" step="0.01" className="mt-1" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="payout-amount" /></div>
            <div><Label>Nota (facoltativa)</Label><Input className="mt-1" value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="es. bonifico settembre" data-testid="payout-note" /></div>
            <Button className="w-full" disabled={payBusy} onClick={doPayout} data-testid="payout-submit">{payBusy ? "Registrazione…" : "Registra come liquidato"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!histAgent} onOpenChange={(o) => !o && setHistAgent(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto" data-testid="payout-history-dialog">
          <DialogHeader><DialogTitle>Storico liquidazioni · {histAgent?.name}</DialogTitle></DialogHeader>
          <div className="divide-y divide-border">
            {hist.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 py-2.5 text-sm" data-testid={`hist-row-${p.id}`}>
                <div><p>{p.note || "Liquidazione"}</p><p className="text-xs text-muted-foreground">{p.created_at ? new Date(p.created_at).toLocaleString("it-IT") : ""}</p></div>
                <span className="font-mono font-semibold text-teal-500">{eur(p.amount)}</span>
              </div>
            ))}
            {hist.length === 0 && <p className="text-sm text-muted-foreground py-4">Nessuna liquidazione registrata.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
